/**
 * Pure helpers for the AI Fashion pose-tile cache signature.
 *
 * The pose-tile fetch in `AIFashionHandler` is keyed by the tuple
 * (model template, background prompt, aspect ratio, product). This file
 * holds the canonical signature shape and equality / key helpers so
 * extracted code paths and tests can reason about cache identity without
 * pulling the handler. No editor or canvas dependencies live here.
 */

import type { AIFashionSession } from './aiFashionSession'

export interface PoseCacheSignature {
  modelTemplateId: string | undefined
  backgroundId: string | undefined
  aspectRatioOption: string | undefined
  productId: string | undefined
}

export function poseCacheSignaturesEqual(
  a: PoseCacheSignature | null,
  b: PoseCacheSignature | null,
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.modelTemplateId === b.modelTemplateId &&
    a.backgroundId === b.backgroundId &&
    a.aspectRatioOption === b.aspectRatioOption &&
    a.productId === b.productId
  )
}

export function poseCacheSignatureKey(sig: PoseCacheSignature): string {
  return `${sig.modelTemplateId ?? ''}|${sig.backgroundId ?? ''}|${sig.aspectRatioOption ?? ''}|${sig.productId ?? ''}`
}

/**
 * Build a `PoseCacheSignature` from the current session params. The tuple
 * is the same one the handler used to compute inline; centralizing it here
 * keeps the four key fields in one place so a future param addition
 * cannot accidentally diverge between cache invalidation and lookup.
 */
export function getPoseSignatureFromSession(session: AIFashionSession): PoseCacheSignature {
  return {
    modelTemplateId: session.selectedTemplate?.id,
    backgroundId: session.backgroundPromptOverride ?? undefined,
    aspectRatioOption: session.aspectRatioOption ?? undefined,
    productId: session.productImageCacheKey ?? undefined,
  }
}
